/**
 * The Upload Studio's file uploads, kept above the canvas (Phase 91, 91-07
 * logic, moved here by 91-09). No React.
 *
 * One entry per key (product + side + tone on the page, see `uploadKeyOf`).
 * The canvas screen remounts for every product of the walk, and Save and
 * next moves on while a file is still uploading, so an upload must outlive
 * the screen that started it: the page owns one store, the screens read it
 * by key, and the save queue waits on it for the stored file's sha256. A
 * screen with no page store (tests, any lone mount) keeps a private store
 * that it disposes on unmount, as 91-07 did.
 *
 * Per entry, as 91-07 built it:
 *  - Drop / pick: the PNG header gives the name, px and MB at once (24 bytes,
 *    `readPngDimensions`); a non-PNG, an unreadable header, over 100 MB, over
 *    60 MP or over 12000 px a side is refused before any byte uploads (the
 *    entry keeps the file it had). Then, side by side: the preview (Manual
 *    orders' `createPreviewUrl` at a 1600 px long edge, no worker) and the
 *    signed PUT (`mintSource`, an XHR PUT with progress). The sha256 is hashed
 *    once the preview is drawn, while the PUT runs, and `commitSource` sends
 *    it with the upload id. A commit cut by the network or a 502 / 504 is sent
 *    once more with the same upload id and sha256.
 *  - Drive: `importSourceFromDrive` with a file id from the org's own list.
 *  - A failed upload keeps the file (and its preview) so placing goes on and
 *    Try again resends it.
 *
 * Offline (91-09): an upload that fails while the browser is offline is
 * 'paused', not failed ("Paused, offline"); coming back online resends every
 * paused one.
 */
import {
  MANUAL_ARTWORK_MAX_BYTES,
  MANUAL_ARTWORK_MAX_PIXELS,
  MANUAL_ARTWORK_MAX_SIDE_PX,
  type TrimBox,
} from '@threadheads/shared';
import { readPngDimensions } from '@/lib/png-dimensions';
import { createPreviewUrl } from '@/features/manual-orders/lib/preview-image';
import { putWithProgress, sha256Hex } from '@/features/product-sync/hooks/useVariantArtworkUpload';
import { isRetryableCommitError, studioErrorLine, type StudioErrorLine } from './studio-errors';

/** The preview's long edge (UI-SPEC section 3, "Loading a big file"). */
export const STUDIO_PREVIEW_MAX_SIDE_PX = 1600;

export interface StudioFile {
  /** 'drop' (picked or dropped here) or 'drive' (imported from the designs folder). */
  origin: 'drop' | 'drive';
  fileName: string;
  /** Bytes, when known (a dropped file; a Drive file the list sized). */
  byteSize: number | null;
  widthPx: number;
  heightPx: number;
  /** A local object URL or the stored preview; null while the preview is made. */
  previewUrl: string | null;
  /** The server's trim box once stored; null before (the whole image is drawn). */
  trim: TrimBox | null;
  /** The stored original's sha256, once the server stored it. */
  sha256: string | null;
  /**
   * Wave 5 review fix 1: which file this is on its key (unique in the
   * store, new for every drop, pick or Drive import; a Try again keeps it).
   * A queued save records it, so a newer file on the same key is never
   * published with the position placed for the older one.
   */
  fileId?: number;
}

/** 'paused': the browser went offline mid-upload; it resends on reconnect. */
export type StudioUploadPhase = 'idle' | 'uploading' | 'paused' | 'stored' | 'failed';

export interface StudioUploadState {
  phase: StudioUploadPhase;
  file: StudioFile | null;
  /** 0..1 of the PUT. */
  progress: number;
  /** Why the upload failed (phase 'failed'). */
  error: StudioErrorLine | null;
  /** A file refused before upload; stays until the next file. */
  refusal: StudioErrorLine | null;
}

export interface DriveImportRequest {
  designCode: string;
  fileId: string;
  fileName: string;
  byteSize: number | null;
}

/** The three procedures an upload calls (the page's own mutations). */
export interface StudioUploadProcedures {
  mint: (input: { locationId: string; fileName: string; byteSize: number }) => Promise<{ uploadId: string; uploadUrl: string }>;
  commit: (input: { locationId: string; uploadId: string; sha256: string; fileName: string }) => Promise<{
    sha256: string;
    widthPx: number;
    heightPx: number;
    trim: TrimBox | null;
    previewUrl: string | null;
  }>;
  importDrive: (input: { locationId: string; designCode: string; fileId: string }) => Promise<{
    fileName: string;
    widthPx: number;
    heightPx: number;
    previewUrl: string | null;
    trim: TrimBox | null;
    sha256: string;
  }>;
}

export const IDLE_UPLOAD: StudioUploadState = { phase: 'idle', file: null, progress: 0, error: null, refusal: null };

/** The page's key for one product's side + tone file. */
export function uploadKeyOf(productId: string, groupKey: string): string {
  return `${productId}|${groupKey}`;
}

interface LastAttempt {
  file: File;
  uploadId: string | null;
  sha256: string | null;
  /** The PUT finished: a retry only needs the commit. */
  putDone: boolean;
}

interface Entry {
  state: StudioUploadState;
  /** A newer file always wins over a slower older one. */
  run: number;
  /** Drive imports race only each other; a refused one leaves a running drop alone. */
  driveRun: number;
  localUrl: string | null;
  abort: AbortController | null;
  last: LastAttempt | null;
  locationId: string | null;
  /** Final fix A5: the user + org it was started in. */
  scope: string | null;
}

/** One file moving, for the tray. */
export interface StudioUploadRow {
  key: string;
  state: StudioUploadState;
}

export interface StudioUploadStore {
  get: (key: string) => StudioUploadState;
  subscribe: (listener: () => void) => () => void;
  /** Every file still uploading, paused or failed (the tray), in start order. */
  moving: () => readonly StudioUploadRow[];
  setProcedures: (procedures: StudioUploadProcedures) => void;
  pick: (key: string, file: File, locationId: string | null) => Promise<void>;
  importFromDrive: (key: string, req: DriveImportRequest, locationId: string | null) => Promise<StudioErrorLine | null>;
  retry: (key: string) => Promise<void>;
  /**
   * Wave 5 review fix 5: take a failed file off the tray. Its slot has no
   * file again; a save waiting on it fails visibly (its toast has Open it).
   * Does nothing for a file still moving or stored.
   */
  dismiss: (key: string) => void;
  /**
   * A stored file whose save went through (the product now prints from it):
   * its preview blob URL is revoked and the key emptied, so a long session
   * does not keep every file in memory. Only when the key still holds that
   * very file (`sha256`); a newer file on the key is kept.
   */
  release: (key: string, sha256: string) => void;
  /** The browser's connection changed: back online resends every paused upload. */
  setOnline: (online: boolean) => void;
  /** Final fix A5: where the current user + org is read from (stamped on every new file). */
  setScope: (scopeNow: () => string | null) => void;
  /**
   * Final fix A5: the org (or user) changed. A file of another scope still
   * uploading, paused or failed is cancelled (its commit would land under the
   * wrong org); a stored one is kept for the save that may still need it.
   */
  rescope: () => void;
  /**
   * Final fix A1: the studio page closed. Every file whose key is not in
   * `keys` (no queued save needs it) is let go: an upload is cancelled, its
   * blob preview revoked and the key emptied, so an abandoned drop never
   * comes back as "new" (and is never published) on the next visit.
   */
  retainOnly: (keys: ReadonlySet<string>) => void;
  dispose: () => void;
}

function looksLikePng(file: File): boolean {
  if (file.type) return file.type === 'image/png';
  return file.name.toLowerCase().endsWith('.png');
}

/** The refusal for a file, before any byte uploads, or its px when it may go. */
async function refuseBeforeUpload(file: File): Promise<{ refusal: StudioErrorLine } | { width: number; height: number }> {
  if (!looksLikePng(file)) return { refusal: { key: 'uploadStudio:error.notPng' } };
  if (file.size > MANUAL_ARTWORK_MAX_BYTES) return { refusal: { key: 'uploadStudio:error.tooBig' } };
  const dims = await readPngDimensions(file);
  if (!dims) return { refusal: { key: 'uploadStudio:error.unreadable' } };
  if (
    dims.width * dims.height > MANUAL_ARTWORK_MAX_PIXELS ||
    dims.width > MANUAL_ARTWORK_MAX_SIDE_PX ||
    dims.height > MANUAL_ARTWORK_MAX_SIDE_PX
  ) {
    return { refusal: { key: 'uploadStudio:error.tooManyPixels' } };
  }
  return dims;
}

const isAbort = (err: unknown) => err instanceof DOMException && err.name === 'AbortError';

/** Every new file in any store gets the next id (never reused on the page). */
let nextFileId = 0;

export function createStudioUploadStore(): StudioUploadStore {
  const entries = new Map<string, Entry>();
  const listeners = new Set<() => void>();
  let procedures: StudioUploadProcedures | null = null;
  let online = true;
  let scopeNow: () => string | null = () => null;
  let movingSnapshot: readonly StudioUploadRow[] = [];

  const notify = () => {
    const next: StudioUploadRow[] = [];
    for (const [key, entry] of entries) {
      const phase = entry.state.phase;
      if (entry.state.file && (phase === 'uploading' || phase === 'paused' || phase === 'failed')) {
        next.push({ key, state: entry.state });
      }
    }
    const same = next.length === movingSnapshot.length && next.every((row, i) => row.state === movingSnapshot[i]!.state);
    if (!same) movingSnapshot = next;
    for (const listener of [...listeners]) listener();
  };

  const entryOf = (key: string): Entry => {
    let entry = entries.get(key);
    if (!entry) {
      entry = { state: IDLE_UPLOAD, run: 0, driveRun: 0, localUrl: null, abort: null, last: null, locationId: null, scope: null };
      entries.set(key, entry);
    }
    return entry;
  };

  const setState = (entry: Entry, fn: (prev: StudioUploadState) => StudioUploadState) => {
    const next = fn(entry.state);
    if (next === entry.state) return;
    entry.state = next;
    notify();
  };

  const setLocalUrl = (entry: Entry, url: string | null) => {
    if (entry.localUrl && entry.localUrl !== url) URL.revokeObjectURL(entry.localUrl);
    entry.localUrl = url;
  };

  /** A failure: paused while offline (resent on reconnect), else failed with its line. */
  const failed = (entry: Entry, err: unknown) => {
    const offline = !online || (typeof navigator !== 'undefined' && navigator.onLine === false);
    setState(entry, (prev) =>
      offline ? { ...prev, phase: 'paused', error: null } : { ...prev, phase: 'failed', error: studioErrorLine(err) },
    );
  };

  const commitOnce = async (locationId: string, uploadId: string, sha256: string, fileName: string) => {
    const input = { locationId, uploadId, sha256, fileName };
    try {
      return await procedures!.commit(input);
    } catch (err) {
      if (!isRetryableCommitError(err)) throw err;
      // Same upload id and hash: the server answers from what it stored.
      return procedures!.commit(input);
    }
  };

  const finishCommit = async (entry: Entry, run: number, attempt: LastAttempt) => {
    try {
      const stored = await commitOnce(entry.locationId!, attempt.uploadId!, attempt.sha256!, attempt.file.name.slice(0, 255));
      if (entry.run !== run) return;
      // The file is stored: a retry is never needed again, so its bytes go.
      entry.last = null;
      setState(entry, (prev) => ({
        ...prev,
        phase: 'stored',
        progress: 1,
        error: null,
        file: prev.file && {
          ...prev.file,
          sha256: stored.sha256,
          widthPx: stored.widthPx,
          heightPx: stored.heightPx,
          trim: stored.trim,
          previewUrl: prev.file.previewUrl ?? stored.previewUrl,
        },
      }));
    } catch (err) {
      if (entry.run !== run) return;
      failed(entry, err);
    }
  };

  const upload = async (entry: Entry, run: number, file: File) => {
    const attempt: LastAttempt = { file, uploadId: null, sha256: null, putDone: false };
    entry.last = attempt;
    entry.abort?.abort();
    const abort = new AbortController();
    entry.abort = abort;
    try {
      const locationId = entry.locationId;
      if (!locationId || !procedures) throw new Error('no location');
      const minted = procedures.mint({ locationId, fileName: file.name.slice(0, 255), byteSize: file.size });
      // Awaited below; a refusal while the preview is made must not surface as unhandled.
      minted.catch(() => undefined);
      if (!entry.state.file?.previewUrl) {
        const preview = await createPreviewUrl(file, STUDIO_PREVIEW_MAX_SIDE_PX);
        if (entry.run !== run) {
          URL.revokeObjectURL(preview);
          return;
        }
        setLocalUrl(entry, preview);
        setState(entry, (prev) => (prev.file ? { ...prev, file: { ...prev.file, previewUrl: preview } } : prev));
      }
      // Hashed while the PUT runs, never before the preview.
      const hash = sha256Hex(file);
      hash.catch(() => undefined);
      const { uploadId, uploadUrl } = await minted;
      attempt.uploadId = uploadId;
      await putWithProgress(
        uploadUrl,
        file,
        'image/png',
        (fraction) => {
          if (entry.run === run) setState(entry, (prev) => ({ ...prev, progress: fraction }));
        },
        abort.signal,
      );
      attempt.sha256 = await hash;
      attempt.putDone = true;
    } catch (err) {
      if (entry.run !== run || isAbort(err)) return;
      failed(entry, err);
      return;
    }
    await finishCommit(entry, run, attempt);
  };

  /** Cancel and forget one key: its upload stops, its preview is revoked. */
  const drop = (key: string, entry: Entry) => {
    entry.run += 1;
    entry.driveRun += 1;
    entry.abort?.abort();
    if (entry.localUrl) URL.revokeObjectURL(entry.localUrl);
    entry.localUrl = null;
    entries.delete(key);
  };

  const retry = async (key: string) => {
    const entry = entries.get(key);
    const last = entry?.last;
    if (!entry || !last) return;
    const run = ++entry.run;
    setState(entry, (prev) => ({ ...prev, phase: 'uploading', error: null }));
    if (last.putDone && last.uploadId && last.sha256) await finishCommit(entry, run, last);
    else await upload(entry, run, last.file);
  };

  return {
    get: (key) => entries.get(key)?.state ?? IDLE_UPLOAD,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    moving: () => movingSnapshot,
    setProcedures: (next) => {
      procedures = next;
    },

    pick: async (key, file, locationId) => {
      const entry = entryOf(key);
      const run = ++entry.run;
      const checked = await refuseBeforeUpload(file);
      if (entry.run !== run) return;
      if ('refusal' in checked) {
        setState(entry, (prev) => ({ ...prev, refusal: checked.refusal }));
        return;
      }
      entry.abort?.abort();
      entry.locationId = locationId;
      entry.scope = scopeNow();
      setLocalUrl(entry, null);
      setState(entry, () => ({
        phase: 'uploading',
        progress: 0,
        error: null,
        refusal: null,
        file: {
          origin: 'drop',
          fileName: file.name,
          byteSize: file.size,
          widthPx: checked.width,
          heightPx: checked.height,
          previewUrl: null,
          trim: null,
          sha256: null,
          fileId: ++nextFileId,
        },
      }));
      await upload(entry, run, file);
    },

    importFromDrive: async (key, req, locationId) => {
      if (!locationId || !procedures) return { key: 'uploadStudio:canvas.chooseLocation' };
      const entry = entryOf(key);
      const driveRun = ++entry.driveRun;
      const runAtStart = entry.run;
      const scopeAtStart = scopeNow();
      try {
        const stored = await procedures.importDrive({ locationId, designCode: req.designCode, fileId: req.fileId });
        // A newer import, or a file dropped meanwhile, wins. The org changed meanwhile: dropped (A5).
        if (entry.driveRun !== driveRun || entry.run !== runAtStart || scopeNow() !== scopeAtStart) return null;
        // The key may have been let go while the import ran (the page closed).
        if (entries.get(key) !== entry) return null;
        entry.run += 1;
        entry.abort?.abort();
        entry.last = null;
        entry.locationId = locationId;
        entry.scope = scopeAtStart;
        setLocalUrl(entry, null);
        setState(entry, () => ({
          phase: 'stored',
          progress: 1,
          error: null,
          refusal: null,
          file: {
            origin: 'drive',
            fileName: stored.fileName,
            byteSize: req.byteSize,
            widthPx: stored.widthPx,
            heightPx: stored.heightPx,
            previewUrl: stored.previewUrl,
            trim: stored.trim,
            sha256: stored.sha256,
            fileId: ++nextFileId,
          },
        }));
        return null;
      } catch (err) {
        return studioErrorLine(err);
      }
    },

    retry,

    dismiss: (key) => {
      const entry = entries.get(key);
      if (!entry || entry.state.phase !== 'failed') return;
      drop(key, entry);
      notify();
    },

    release: (key, sha256) => {
      const entry = entries.get(key);
      if (!entry || entry.state.phase !== 'stored' || entry.state.file?.sha256 !== sha256) return;
      drop(key, entry);
      notify();
    },

    setScope: (next) => {
      scopeNow = next;
    },

    rescope: () => {
      const now = scopeNow();
      let any = false;
      for (const [key, entry] of [...entries]) {
        const phase = entry.state.phase;
        if (entry.scope === now || phase === 'stored' || phase === 'idle') continue;
        drop(key, entry);
        any = true;
      }
      if (any) notify();
    },

    retainOnly: (keys) => {
      let any = false;
      for (const [key, entry] of [...entries]) {
        if (keys.has(key)) continue;
        drop(key, entry);
        any = true;
      }
      if (any) notify();
    },

    setOnline: (next) => {
      const cameBack = next && !online;
      online = next;
      if (!cameBack) return;
      for (const [key, entry] of entries) if (entry.state.phase === 'paused') void retry(key);
    },

    dispose: () => {
      for (const entry of entries.values()) {
        entry.run += 1;
        entry.abort?.abort();
        if (entry.localUrl) URL.revokeObjectURL(entry.localUrl);
        entry.localUrl = null;
      }
      entries.clear();
      movingSnapshot = [];
      notify();
    },
  };
}
